import { entityUrl } from '@/lib/entity-url';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  serverTimestamp,
  QueryDocumentSnapshot,
  DocumentData,
  FieldValue,
  documentId,
  getCountFromServer,
  type QueryConstraint,
} from 'firebase/firestore';
import { chunkArray, FIRESTORE_IN_QUERY_LIMIT } from './batch-fetcher';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { Article, ArticleFormData, ArticlesResponse, User } from '../types/content';
import { generateSlug, calculateReadTime } from './utils';
import { revalidationService } from './revalidation-service';
import { imageProcessingService, type ProcessedImage } from './image-processing-service';
import { RBACService } from './rbac-service';
import { Permission } from '../types/permissions';
import { tagService } from './tag-service';
import { stripVectors } from './server/strip-vectors';
// A1 — the generic writers below refuse transition-controlled fields; the audited lane
// (POST /api/articles/transition) is the only thing that may write them.
import { assertNoTransitionOwnedFields } from './publish-lane-guard';
import {
  handleUserError,
  handleSilentError,
  handleDataFetchError
} from './error-handler';

/** A same-slug article doc reduced to what servability ranking needs. */
export interface SlugArticleDoc {
  id: string;
  data: DocumentData;
}

/**
 * Slug-collision servability — PURE, shared by the client-SDK published lookup below and
 * the server-side Admin-SDK fetch (src/lib/server/articles-admin.ts).
 *
 * A slug is NOT unique in `articles` — a trashed re-draft keeps the published article's
 * slug (isDeleted:true, status:'draft'), and naive `limit(1)` picks could 404 a genuinely
 * published article (the cambria-creators case). Given ALL same-slug docs:
 *   - a trashed/deleted doc must NEVER shadow a live article;
 *   - !allowPreview: only THE published, non-deleted doc is servable;
 *   - allowPreview (the /news/[slug] public renderer): rank by public-servability —
 *     published/superseded public (3) > preview/unlisted (2) > everything else (-1).
 *     'superseded' is a runtime status outside the compile-time union (R1 corrections
 *     desk: a superseded article keeps its URL + renders a forward pointer, noindex'd).
 */
export function pickServableArticleDoc(
  docs: SlugArticleDoc[],
  allowPreview: boolean
): SlugArticleDoc | null {
  const candidates = docs.filter((d) => d.data.isDeleted !== true);
  if (candidates.length === 0) return null;

  if (!allowPreview) {
    // Deterministic on a collision — pick THE published, non-deleted doc rather than an
    // arbitrary first match.
    return candidates.find((d) => d.data.status === 'published') ?? null;
  }

  const rank = (data: DocumentData): number => {
    const visibility = (data.visibility as string) || 'public'; // default public (back-compat)
    if (visibility === 'hidden') return -1; // never public-accessible
    if (visibility === 'preview' || visibility === 'unlisted') return 2; // viewable regardless of status
    if (visibility === 'public') {
      return (data.status === 'published' || (data.status as string) === 'superseded') ? 3 : -1;
    }
    return -1;
  };
  let best: SlugArticleDoc | null = null;
  let bestRank = -1;
  for (const d of candidates) {
    const r = rank(d.data);
    if (r > bestRank) { bestRank = r; best = d; }
  }
  if (!best || bestRank < 0) return null; // no servable doc (e.g. only hidden/unpublished remain)
  return best;
}

// Articles Service
export class ArticlesService {
  private collectionName = 'articles';


  // Create a new article (with RBAC security)
  async createArticle(data: ArticleFormData, currentUser: User): Promise<string> {
    try {
      // Check permissions
      await RBACService.authorize(
        currentUser,
        Permission.ARTICLE_CREATE,
        undefined,
        'You do not have permission to create articles'
      );

      // A1 — a brand-new doc created straight into `published`/`scheduled` is a publish with
      // no `transitionLog`, exactly like the three evidence docs. Create the draft, then
      // drive the flip through the audited route. (Mirror of ops.js:556's refusal.)
      assertNoTransitionOwnedFields(data as unknown as Record<string, unknown>, { creation: true });

      // Validate and sanitize form data based on user role
      const sanitizedData = RBACService.validateArticleFormData(currentUser, data);
      // CRITICAL VALIDATION: Check required fields for publishing
      if (sanitizedData.status === 'published') {
        const missingFields = [];
        if (!sanitizedData.title?.trim()) missingFields.push('title');
        if (!sanitizedData.tags || sanitizedData.tags.length === 0) missingFields.push('tags');
        if (!sanitizedData.excerpt?.trim()) missingFields.push('excerpt');
        if (!sanitizedData.content?.trim()) missingFields.push('content');
        if (!sanitizedData.author?.trim()) missingFields.push('author');
        if (!sanitizedData.slug?.trim()) missingFields.push('slug');

        if (missingFields.length > 0) {
          throw new Error(`Cannot publish article. Missing required fields: ${missingFields.join(', ')}`);
        }
      }

      const now = new Date();
      const article: Omit<Article, 'id'> = {
        ...sanitizedData,
        featuredImage: typeof sanitizedData.featuredImage === 'string' ? sanitizedData.featuredImage : null,
        slug: sanitizedData.slug || generateSlug(sanitizedData.title),
        readTime: sanitizedData.readTime || calculateReadTime(sanitizedData.content),
        hasQuiz: sanitizedData.hasQuiz || false,
        isFeaturedQuiz: sanitizedData.isFeaturedQuiz || false,
        isFeatured: sanitizedData.isFeatured || false,
        featuredLocation: sanitizedData.featuredLocation || null, // Fix: Don't allow undefined
        featuredPriority: sanitizedData.featuredPriority || 0,
        // Hero banner system
        isHero: sanitizedData.isHero || false,
        heroPosition: sanitizedData.heroPosition || 0,
        // Podcast integration
        podcastEmbedUrl: sanitizedData.podcastEmbedUrl || null,
        images: [], // Will be populated when images are uploaded
        createdAt: now,
        updatedAt: now,
        publishedAt: sanitizedData.status === 'published' ? now : null,
      };

      const docRef = await addDoc(collection(db!, this.collectionName), {
        ...article,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishedAt: article.publishedAt ? serverTimestamp() : null,
      });

      // Handle tags if provided
      if (sanitizedData.tags && sanitizedData.tags.length > 0) {
        try {
          await tagService.addTagsToArticle(
            docRef.id,
            sanitizedData.tags,
            [], // weights will be calculated based on tag order
            currentUser.id
          );

          // Published-only: a new article counts toward its tags' articleCount only when published
          if (sanitizedData.status === 'published') {
            try {
              await tagService.adjustArticleCounts(sanitizedData.tags, 1);
            } catch (tagCountError) {
              handleSilentError(tagCountError, 'ArticlesService.createArticle - Increment tag articleCount');
            }
          }

          // Add to optimized collections if published
          if (sanitizedData.status === 'published') {
            try {
              const { tagArticleCollectionsService } = await import('./tag-article-collections-service');
              await tagArticleCollectionsService.addArticleToTagCollections(
                docRef.id,
                { ...article, id: docRef.id } as Article,
                sanitizedData.tags
              );
            } catch (collectionError) {
              handleSilentError(collectionError, 'ArticlesService.createArticle - Add to optimized collections');
            }
          }
        } catch (tagError) {
          handleSilentError(tagError, 'ArticlesService.createArticle - Add tags');
        }
      }

      // Invalidate carousel caches when new articles are created
      try {
        const { carouselService } = await import('./carousel-service');
        carouselService.invalidateCarouselCaches();
      } catch (carouselCacheError) {
        handleSilentError(carouselCacheError, 'ArticlesService.createArticle - Invalidate carousel caches');
      }

      // Trigger client-side cache invalidation for Recent Articles widgets
      try {
        // Dispatch a custom event to notify Recent Articles components to refresh
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('articleUpdated', {
            detail: { articleId: docRef.id, action: 'create' }
          }));
        }
      } catch (eventError) {
        handleSilentError(eventError, 'ArticlesService.createArticle - Dispatch articleUpdated event');
      }

      // Update user stats (increment article count ONLY for published articles)
      if (sanitizedData.status === 'published') {
        try {
          const { userManagementService } = await import('./user-management-service');
          await userManagementService.incrementArticleCount(sanitizedData.author);
        } catch (statsError) {
          handleSilentError(statsError, 'ArticlesService.createArticle - Update user stats');
        }
      }

      // Log activity
      try {
        const { activityLogService } = await import('./activity-log-service');
        await activityLogService.logArticleCreated(
          currentUser,
          docRef.id,
          sanitizedData.title
        );
      } catch (logError) {
        handleSilentError(logError, 'ArticlesService.createArticle - Activity logging');
      }

      // If article was published immediately, revalidate homepage and article lists
      if (sanitizedData.status === 'published') {
        try {
          const { revalidatePath, revalidateTag } = await import('next/cache');
          await revalidatePath('/');
          await revalidateTag('articles-list');
          await revalidateTag('read-data');
          console.log('✅ Revalidated homepage and article lists after creating published article');
        } catch (revalidateError) {
          handleSilentError(revalidateError, 'ArticlesService.createArticle - On-demand revalidation');
        }
      }

      return docRef.id;
    } catch (error) {
      throw handleUserError(error, 'Failed to create article');
    }
  }

  // Republish an article (updates published date to current time)
  async republishArticle(id: string, currentUser: User): Promise<void> {
    // Get existing article to check ownership and ensure it's published
    const existingArticle = await this.getArticle(id);
    if (!existingArticle) {
      throw new Error('Article not found');
    }

    if (existingArticle.status !== 'published') {
      throw new Error('Only published articles can be republished');
    }

    // Check permissions for republishing (same as publish permissions)
    const canPublish = RBACService.checkArticlePermission(currentUser, 'publish', existingArticle.author);
    if (!canPublish) {
      const normalizedAuthorId = existingArticle.author;
      const normalizedCurrentUserId = currentUser.id;
      const isOwnArticle = normalizedAuthorId === normalizedCurrentUserId;
      const permission = isOwnArticle ? Permission.ARTICLE_PUBLISH_OWN : Permission.ARTICLE_PUBLISH_ALL;
      throw new Error(RBACService.getPermissionErrorMessage(permission, currentUser.role));
    }

    // Update only the publishedAt timestamp and updatedAt
    const updateData = {
      publishedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      const docRef = doc(db!, this.collectionName, id);
      await updateDoc(docRef, updateData);

      // CRITICAL: Re-sync to optimized collections to update publishedAt timestamp
      // This ensures carousels show the article with the new publish date
      if (existingArticle.tags && existingArticle.tags.length > 0) {
        try {
          const { tagArticleCollectionsService } = await import('./tag-article-collections-service');
          console.log(`📦 RepublishArticle: Re-syncing article ${id} to optimized collections`);

          // Remove old entry
          await tagArticleCollectionsService.removeArticleFromTagCollections(id);

          // Add back with updated timestamp
          const updatedArticle = {
            ...existingArticle,
            publishedAt: new Date(), // Use the new publish date
            updatedAt: new Date(),
            id
          } as Article;

          await tagArticleCollectionsService.addArticleToTagCollections(
            id,
            updatedArticle,
            existingArticle.tags
          );
        } catch (collectionError) {
          handleSilentError(collectionError, 'ArticlesService.republishArticle - Sync optimized collections');
        }
      }

      // Trigger revalidation for republished content
      try {
        await revalidationService.revalidateArticle(id, existingArticle.slug);
      } catch (revalidationError) {
        handleSilentError(revalidationError, 'ArticlesService.republishArticle - Revalidation');
      }

      // Invalidate carousel caches when articles are republished
      try {
        const { carouselService } = await import('./carousel-service');
        carouselService.invalidateCarouselCaches();
      } catch (carouselCacheError) {
        handleSilentError(carouselCacheError, 'ArticlesService.republishArticle - Invalidate carousel caches');
      }

      // Trigger client-side cache invalidation for Recent Articles widgets
      try {
        // Use a timeout to ensure we're in the client environment
        if (typeof window !== 'undefined') {
          console.log('🚀 Dispatching articleUpdated event for republish:', { articleId: id, action: 'republish' });

          // Dispatch immediately
          window.dispatchEvent(new CustomEvent('articleUpdated', {
            detail: { articleId: id, action: 'republish' }
          }));

          // Also dispatch after a brief delay to ensure components are ready
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('articleUpdated', {
              detail: { articleId: id, action: 'republish' }
            }));
          }, 100);
        } else {
          console.log('⚠️ Window not available, scheduling event dispatch for next tick');
          // If window not available, try again on next tick
          setTimeout(() => {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('articleUpdated', {
                detail: { articleId: id, action: 'republish' }
              }));
            }
          }, 0);
        }
      } catch (eventError) {
        handleSilentError(eventError, 'ArticlesService.republishArticle - Dispatch articleUpdated event');
      }
    } catch (error) {
      throw handleUserError(error, 'Failed to republish article');
    }
  }

  /**
   * A1 — the denormalized bookkeeping that follows a publish.
   *
   * The status flip itself is NOT here: it belongs to the audited route
   * (POST /api/articles/transition), which stamps the transition audit, promotes
   * `visibility` hidden→public and logs the `article_published` activity server-side. What
   * remains are the client-plane denormalizations this service has always owned — the
   * author's article counter, the published-only tag counter, the optimized per-tag
   * collections the carousels read, and the cache/event fan-out. The UI calls this
   * immediately after the audited transition returns OK, so the reroute is behaviour-neutral
   * (these used to run inside `updateArticle`'s publish branch).
   *
   * Every step is best-effort: a bookkeeping failure must never look like a failed publish
   * (the publish already committed).
   */
  async applyPublishSideEffects(id: string): Promise<void> {
    const article = await this.getArticle(id);
    if (!article) return;
    const tags = article.tags || [];

    // Author's published-article counter.
    try {
      const { userManagementService } = await import('./user-management-service');
      await userManagementService.incrementArticleCount(article.author);
    } catch (statsError) {
      handleSilentError(statsError, 'ArticlesService.applyPublishSideEffects - Increment article count');
    }

    // Published-only tag counter.
    try {
      if (tags.length > 0) await tagService.adjustArticleCounts(tags, 1);
    } catch (tagCountError) {
      handleSilentError(tagCountError, 'ArticlesService.applyPublishSideEffects - Increment tag articleCount');
    }

    // Optimized per-tag collections (the carousel read path).
    try {
      if (tags.length > 0) {
        const { tagArticleCollectionsService } = await import('./tag-article-collections-service');
        await tagArticleCollectionsService.removeArticleFromTagCollections(id);
        await tagArticleCollectionsService.addArticleToTagCollections(id, article, tags);
      }
    } catch (collectionError) {
      handleSilentError(collectionError, 'ArticlesService.applyPublishSideEffects - Sync optimized collections');
    }

    try {
      const { carouselService } = await import('./carousel-service');
      carouselService.invalidateCarouselCaches();
    } catch (carouselCacheError) {
      handleSilentError(carouselCacheError, 'ArticlesService.applyPublishSideEffects - Invalidate carousel caches');
    }

    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('articleUpdated', { detail: { articleId: id, action: 'publish' } }));
      }
    } catch (eventError) {
      handleSilentError(eventError, 'ArticlesService.applyPublishSideEffects - Dispatch articleUpdated event');
    }
  }

  // Update an existing article (with RBAC security)
  async updateArticle(id: string, data: Partial<ArticleFormData>, currentUser: User): Promise<void> {
    try {
      // Get existing article to check ownership
      const existingArticle = await this.getArticle(id);
      if (!existingArticle) {
        throw new Error('Article not found');
      }

      // Check permissions based on ownership
      // Normalize IDs for comparison (handle both string and object cases)
      const normalizedCurrentUserId = typeof currentUser.id === 'string' ? currentUser.id.trim() : currentUser.id;
      const normalizedAuthorId = typeof existingArticle.author === 'string' ? existingArticle.author.trim() : existingArticle.author;

      const canEdit = RBACService.checkArticlePermission(currentUser, 'edit', existingArticle.author);
      if (!canEdit) {
        const isOwnArticle = normalizedAuthorId === normalizedCurrentUserId;
        const permission = isOwnArticle ? Permission.ARTICLE_EDIT_OWN : Permission.ARTICLE_EDIT_ALL;
        throw new Error(RBACService.getPermissionErrorMessage(permission, currentUser.role));
      }

      // A1 — THE GUARD. This is the generic updater: it accepts a caller-supplied field bag
      // from three doors (the list-row publish button, the editor form, and the arbitrary
      // JSON body of PUT /api/articles/[id]) and, before this, wrote `status` straight to
      // Firestore with no transition audit — the writer behind the three `published` +
      // `transitionLog:undefined` docs in the defect register (§A1). It now REFUSES the
      // transition-controlled fields and names the audited lane, exactly as the operator CLI
      // has always done (backend/scripts/ops.js:556 "refusing to patch status via
      // patch-field"). Runs AFTER the RBAC throw above so permission errors stay first (UX),
      // and BEFORE any write. A same-status echo (every content save sends the current
      // status back) is NOT a transition and passes through untouched.
      assertNoTransitionOwnedFields(data as unknown as Record<string, unknown>, {
        currentStatus: existingArticle.status,
      });

      // Validate and sanitize form data
      const sanitizedData = RBACService.validateArticleFormData(
        currentUser,
        data,
        true,
        existingArticle.author
      );

      // CRITICAL VALIDATION: a published article must never be edited into an incomplete
      // state. (Pre-A1 this also covered the draft→published flip; that flip now happens in
      // the audited route, which validates before transitioning.)
      if (sanitizedData.status === 'published') {
        // Merge existing data with updates to check completeness
        const finalData = { ...existingArticle, ...sanitizedData };

        const missingFields = [];
        if (!finalData.title?.trim()) missingFields.push('title');
        if (!finalData.tags || finalData.tags.length === 0) missingFields.push('tags');
        if (!finalData.excerpt?.trim()) missingFields.push('excerpt');
        if (!finalData.content?.trim()) missingFields.push('content');
        if (!finalData.author?.trim()) missingFields.push('author');
        if (!finalData.slug?.trim()) missingFields.push('slug');

        if (missingFields.length > 0) {
          throw new Error(`Cannot publish article. Missing required fields: ${missingFields.join(', ')}`);
        }
      }

      // Build update data with proper typing for Firestore serverTimestamp()
      const updateData: Record<string, unknown> = {
        ...sanitizedData,
        updatedAt: serverTimestamp(),
      };

      // Handle featuredImage properly - only include if it's a string URL
      if (sanitizedData.featuredImage !== undefined) {
        updateData.featuredImage = typeof sanitizedData.featuredImage === 'string' ? sanitizedData.featuredImage : null;
      }

      // Handle featuredLocation properly - only include if article is featured
      if (sanitizedData.isFeatured !== undefined) {
        if (sanitizedData.isFeatured && sanitizedData.featuredLocation) {
          updateData.featuredLocation = sanitizedData.featuredLocation;
        } else if (!sanitizedData.isFeatured) {
          delete updateData.featuredLocation; // Remove field instead of setting to null
        }
      }

      // Handle podcastEmbedUrl - explicitly include if provided
      if (sanitizedData.podcastEmbedUrl !== undefined) {
        updateData.podcastEmbedUrl = sanitizedData.podcastEmbedUrl || null;
      }

      // A1 — a status CHANGE can no longer reach this point: the guard above refuses it, so
      // `sanitizedData.status` is at most a same-value echo from the form. The publish
      // bookkeeping that used to hang off this branch (activity log, author article count,
      // tag articleCount) moved to `applyPublishSideEffects`, which the UI calls after the
      // AUDITED transition succeeds. The matching unpublish branch is gone with it:
      // `published→draft` is not a legal articles transition (shared/publish/transitions.js
      // :234), so nothing can perform it.
      const wasPublished = existingArticle.status === 'published';
      const isNowPublished = sanitizedData.status === 'published';

      // Editing an article that IS published still requires publish authority (unchanged —
      // this used to double as the publish gate, and remains the edit gate for live docs).
      if (isNowPublished) {
        RBACService.debugPermissionCheck(currentUser, 'publish', existingArticle.author, 'UpdateArticle Published Edit');

        const canPublish = RBACService.checkArticlePermission(currentUser, 'publish', existingArticle.author);
        if (!canPublish) {
          const isOwnArticle = normalizedAuthorId === normalizedCurrentUserId;
          const permission = isOwnArticle ? Permission.ARTICLE_PUBLISH_OWN : Permission.ARTICLE_PUBLISH_ALL;
          throw new Error(RBACService.getPermissionErrorMessage(permission, currentUser.role));
        }
      }

      // Recalculate read time if content changed
      if (sanitizedData.content) {
        updateData.readTime = calculateReadTime(sanitizedData.content);
      }

      const docRef = doc(db!, this.collectionName, id);
      await updateDoc(docRef, updateData as any); // Type assertion needed for Firestore's complex type requirements

      // Log article update activity
      // Only skip logging if this is a status-only change (those are logged separately)
      const hasContentChanges = !!(
        sanitizedData.title ||
        sanitizedData.content ||
        sanitizedData.excerpt ||
        sanitizedData.tags ||
        sanitizedData.featuredImage ||
        sanitizedData.category
      );

      // A1 — the old `statusChanged && !publishingStatusChange` arm is unreachable now that
      // the guard refuses status changes here; transitions are logged by the audited route.
      if (hasContentChanges) {
        try {
          const { activityLogService } = await import('./activity-log-service');
          const changes = [];
          if (sanitizedData.title) changes.push('title');
          if (sanitizedData.content) changes.push('content');
          if (sanitizedData.excerpt) changes.push('excerpt');
          if (sanitizedData.tags) changes.push('tags');
          if (sanitizedData.featuredImage) changes.push('image');
          if (sanitizedData.category) changes.push('category');

          await activityLogService.logActivity(
            'article_updated',
            currentUser,
            'article',
            id,
            sanitizedData.title || existingArticle.title,
            {
              status: sanitizedData.status || existingArticle.status,
              changedFields: changes.join(', '),
              editorRole: currentUser.role,
            },
            'admin' // Changed to admin since we want to track all edits
          );
        } catch (logError) {
          handleSilentError(logError, 'ArticlesService.updateArticle - Log update activity');
        }
      }

      // Handle tags and collection updates if provided
      if (sanitizedData.tags !== undefined) {
        try {
          await tagService.addTagsToArticle(
            id,
            sanitizedData.tags,
            [], // weights will be calculated based on tag order
            currentUser.id
          );

          // Tag edit on an article that STAYS published → adjust the published-only
          // count by the tag delta. (Publish/unpublish transitions are counted above;
          // a draft staying a draft never affects the count.)
          try {
            const finalStatus = sanitizedData.status !== undefined ? sanitizedData.status : existingArticle.status;
            if (existingArticle.status === 'published' && finalStatus === 'published') {
              const oldTags = existingArticle.tags || [];
              const newTags = sanitizedData.tags || [];
              const added = newTags.filter((t: string) => !oldTags.includes(t));
              const removed = oldTags.filter((t: string) => !newTags.includes(t));
              if (added.length) await tagService.adjustArticleCounts(added, 1);
              if (removed.length) await tagService.adjustArticleCounts(removed, -1);
            }
          } catch (tagCountError) {
            handleSilentError(tagCountError, 'ArticlesService.updateArticle - Adjust tag articleCount on tag edit');
          }

          // Update optimized collections
          try {
            const { tagArticleCollectionsService } = await import('./tag-article-collections-service');

            // Remove from old collections first
            await tagArticleCollectionsService.removeArticleFromTagCollections(id);

            // Add to new collections if published and has tags
            const finalStatus = sanitizedData.status || existingArticle.status;
            if (finalStatus === 'published' && sanitizedData.tags.length > 0) {
              const updatedArticle = { ...existingArticle, ...sanitizedData, id } as Article;
              await tagArticleCollectionsService.addArticleToTagCollections(
                id,
                updatedArticle,
                sanitizedData.tags
              );
            }
          } catch (collectionError) {
            handleSilentError(collectionError, 'ArticlesService.updateArticle - Update optimized collections');
          }
        } catch (tagError) {
          handleSilentError(tagError, 'ArticlesService.updateArticle - Update tags');
        }
      }

      // CRITICAL FIX: Always sync optimized collections for published articles
      // This ensures carousels show the latest articles even when only content/metadata changes
      // Only sync if tags weren't just updated above (to avoid double-sync)
      if (sanitizedData.tags === undefined) {
        try {
          const { tagArticleCollectionsService } = await import('./tag-article-collections-service');
          const finalStatus = sanitizedData.status !== undefined ? sanitizedData.status : existingArticle.status;
          const finalTags = existingArticle.tags || [];

          if (finalStatus === 'published' && finalTags.length > 0) {
            // Article is published with tags - ensure it's in optimized collections
            console.log(`📦 UpdateArticle: Syncing article ${id} to optimized collections (status: ${finalStatus}, tags: ${finalTags.length})`);
            await tagArticleCollectionsService.removeArticleFromTagCollections(id);
            const updatedArticle = { ...existingArticle, ...sanitizedData, id } as Article;
            await tagArticleCollectionsService.addArticleToTagCollections(
              id,
              updatedArticle,
              finalTags
            );
          } else if (finalStatus !== 'published') {
            // Article is unpublished - remove from collections
            console.log(`📦 UpdateArticle: Removing article ${id} from optimized collections (status: ${finalStatus})`);
            await tagArticleCollectionsService.removeArticleFromTagCollections(id);
          }
        } catch (collectionError) {
          handleSilentError(collectionError, 'ArticlesService.updateArticle - Sync optimized collections');
        }
      }

      // Trigger revalidation for the updated article
      try {
        if (existingArticle.slug) {
          await revalidationService.revalidateArticle(
            existingArticle.slug,
            id
          );

          // If this is a featured article update, also revalidate featured content
          if (sanitizedData.isFeatured !== undefined || sanitizedData.featuredImage !== undefined) {
            await revalidationService.revalidateFeatured();
          }

          // If article was just published, revalidate homepage and article lists
          if (!wasPublished && isNowPublished) {
            try {
              const { revalidatePath, revalidateTag } = await import('next/cache');
              await revalidatePath('/');
              await revalidateTag('articles-list');
              await revalidateTag('read-data');
              console.log('✅ Revalidated homepage and article lists after publishing article');
            } catch (revalidateError) {
              handleSilentError(revalidateError, 'ArticlesService.updateArticle - On-demand revalidation');
            }
          }
          
          // CRITICAL: If article was just unpublished, revalidate to remove from feeds
          if (wasPublished && !isNowPublished) {
            try {
              const { revalidatePath, revalidateTag } = await import('next/cache');
              await revalidatePath('/');
              await revalidateTag('articles-list');
              await revalidateTag('read-data');
              
              // Revalidate tag pages for each tag the article has
              if (existingArticle.tags && existingArticle.tags.length > 0) {
                const { tagService } = await import('./tag-service');
                for (const tagId of existingArticle.tags) {
                  try {
                    const tag = await tagService.getTag(tagId);
                    if (tag?.slug) {
                      await revalidatePath(entityUrl(tag.slug, { trailingSlash: false }));
                    }
                  } catch (tagError) {
                    // Continue with other tags if one fails
                  }
                }
              }
              console.log('✅ Revalidated pages after unpublishing article');
            } catch (revalidateError) {
              handleSilentError(revalidateError, 'ArticlesService.updateArticle - Unpublish revalidation');
            }
          }

          // CRITICAL: If visibility changed from public to non-public (or vice versa), revalidate
          const oldVisibility = existingArticle.visibility || 'public';
          const newVisibility = sanitizedData.visibility || oldVisibility;
          const visibilityChanged = sanitizedData.visibility !== undefined && oldVisibility !== newVisibility;
          
          if (visibilityChanged && isNowPublished) {
            try {
              const { revalidatePath, revalidateTag } = await import('next/cache');
              await revalidatePath('/');
              await revalidateTag('articles-list');
              await revalidateTag('read-data');
              
              // Revalidate tag pages for each tag the article has
              if (existingArticle.tags && existingArticle.tags.length > 0) {
                const { tagService } = await import('./tag-service');
                for (const tagId of existingArticle.tags) {
                  try {
                    const tag = await tagService.getTag(tagId);
                    if (tag?.slug) {
                      await revalidatePath(entityUrl(tag.slug, { trailingSlash: false }));
                    }
                  } catch (tagError) {
                    // Continue with other tags if one fails
                  }
                }
              }
              console.log(`✅ Revalidated pages after visibility change: ${oldVisibility} → ${newVisibility}`);
            } catch (revalidateError) {
              handleSilentError(revalidateError, 'ArticlesService.updateArticle - Visibility change revalidation');
            }
          }
        }
      } catch (revalidationError) {
        handleSilentError(revalidationError, 'ArticlesService.updateArticle - Revalidation');
      }

      // Invalidate carousel caches when articles are updated (especially for featured image changes or visibility changes)
      try {
        const { carouselService } = await import('./carousel-service');
        carouselService.invalidateCarouselCaches();
      } catch (carouselCacheError) {
        handleSilentError(carouselCacheError, 'ArticlesService.updateArticle - Invalidate carousel caches');
      }

      // GLOBAL CACHE INVALIDATION SYSTEM
      // Trigger client-side cache invalidation for Recent Articles widgets
      // See: docs/GLOBAL_CACHE_INVALIDATION_SYSTEM.md
      try {
        // Dispatch 'articleUpdated' event for content changes
        // Components listening: Recent Articles widgets, content-dependent components
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('articleUpdated', {
            detail: { articleId: id, action: 'update' }
          }));
        }
      } catch (eventError) {
        handleSilentError(eventError, 'ArticlesService.updateArticle - Dispatch articleUpdated event');
      }
    } catch (error) {
      throw handleUserError(error, 'Failed to update article');
    }
  }

  // Get a single article by ID
  async getArticle(id: string): Promise<Article | null> {
    try {
      const docRef = doc(db!, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...stripVectors(data),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          publishedAt: data.publishedAt?.toDate(),
        } as Article;
      }

      return null;
    } catch (error) {
      return handleDataFetchError(error, 'ArticlesService.getArticle', id);
    }
  }

  /**
   * Get multiple articles by IDs in batch (performance optimization)
   * 
   * Uses Firestore `in` queries with chunking (30 items max per query)
   * for significantly better performance than individual getDoc calls.
   * 
   * Phase 1 Optimization: Reduced from N individual getDoc calls to
   * ceil(N/30) batched queries.
   * 
   * @param articleIds - Array of article IDs to fetch
   * @param options - Optional filters (publishedOnly, publicOnly)
   * @returns Map of articleId -> Article for O(1) lookup
   */
  // ⚠ RULES: this documentId-in query carries no status constraint, so an
  // UNAUTHENTICATED caller is rejected by the `articles` rules (post-2026-08-28
  // hardening). Server pages must use getArticlesBatchServer
  // (src/lib/server/articles-admin.ts); this client path is for authenticated UI only.
  async getArticlesBatch(
    articleIds: string[],
    options: {
      publishedOnly?: boolean;
      publicOnly?: boolean;
    } = {}
  ): Promise<Map<string, Article>> {
    const articleMap = new Map<string, Article>();
    const { publishedOnly = false, publicOnly = false } = options;

    if (!db || articleIds.length === 0) {
      return articleMap;
    }

    try {
      const uniqueIds = [...new Set(articleIds)];
      const chunks = chunkArray(uniqueIds, FIRESTORE_IN_QUERY_LIMIT);
      const articlesCollection = collection(db, this.collectionName);

      const chunkPromises = chunks.map(async (chunkIds) => {
        try {
          const q = query(articlesCollection, where(documentId(), 'in', chunkIds));
          const snapshot = await getDocs(q);
          return snapshot.docs;
        } catch (error) {
          handleSilentError(error, `ArticlesService.getArticlesBatch - chunk of ${chunkIds.length}`);
          return [];
        }
      });

      const chunkResults = await Promise.all(chunkPromises);

      for (const docs of chunkResults) {
        for (const docSnap of docs) {
          const data = docSnap.data();

          // Apply filters
          if (publishedOnly && data.status !== 'published') {
            continue;
          }
          if (publicOnly && data.visibility && data.visibility !== 'public') {
            continue;
          }

          const article: Article = {
            id: docSnap.id,
            ...stripVectors(data),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            publishedAt: data.publishedAt?.toDate(),
          } as Article;

          articleMap.set(docSnap.id, article);
        }
      }

      return articleMap;
    } catch (error) {
      handleSilentError(error, 'ArticlesService.getArticlesBatch');
      return articleMap;
    }
  }

  // Get published article by ID (public access - only published articles)
  async getPublishedArticle(id: string): Promise<Article | null> {
    try {
      // Check if db is available (may not be during build time)
      if (!db) {
        return null;
      }

      const docRef = doc(db!, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();

        // Only return if article is published
        if (data.status !== 'published') {
          return null;
        }

        return {
          id: docSnap.id,
          ...stripVectors(data),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          publishedAt: data.publishedAt?.toDate(),
        } as Article;
      }

      return null;
    } catch (error) {
      return handleDataFetchError(error, 'ArticlesService.getPublishedArticle', id);
    }
  }

  // Get article by slug (public access — PUBLISHED docs only).
  //
  // This is the CLIENT-SDK path: it runs unauthenticated on the Next server (SSR and
  // build-time prerender), and the `articles` rules only admit an unauthenticated list
  // when the query provably returns status=='published' docs. Preview / unlisted /
  // superseded slugs are served by the server-side Admin-SDK fetch
  // (src/lib/server/articles-admin.ts:getArticleBySlugServer), which bypasses rules.
  async getArticleBySlug(slug: string): Promise<Article | null> {
    try {
      // Check if db is available (may not be during build time)
      if (!db) {
        return null;
      }

      // SLUG-COLLISION HARDENING (SB Phase-2, 2026-07-17): a slug is NOT unique in
      // `articles` — a trashed re-draft keeps the published article's slug. The status
      // filter drops draft shadows server-side; pickServableArticleDoc still drops any
      // trashed-but-published doc and picks deterministically. `limit(20)` bounds the
      // (near-unique) slug fanout. Single-field equalities — no composite index needed.
      const querySnapshot = await getDocs(query(
        collection(db!, this.collectionName),
        where('slug', '==', slug),
        where('status', '==', 'published'),
        limit(20)
      ));
      if (querySnapshot.empty) return null;

      const picked = pickServableArticleDoc(
        querySnapshot.docs.map((d) => ({ id: d.id, data: d.data() as DocumentData })),
        false
      );
      if (!picked) return null;

      return {
        id: picked.id,
        ...stripVectors(picked.data),
        createdAt: picked.data.createdAt?.toDate() || new Date(),
        updatedAt: picked.data.updatedAt?.toDate() || new Date(),
        publishedAt: picked.data.publishedAt?.toDate(),
      } as Article;
    } catch (error) {
      return handleDataFetchError(error, 'ArticlesService.getArticleBySlug', slug);
    }
  }

  // Get published articles by tag (public access - uses optimized collections)
  async getPublishedArticlesByTag(tagId: string, limitCount: number = 20): Promise<Article[]> {
    try {
      // Check if db is available (may not be during build time)
      if (!db) {
        return [];
      }

      // Use optimized collections for consistency and performance
      // This already has built-in fallback to direct Firestore queries if optimized collections fail
      const response = await this.getArticlesByTagsOptimized([tagId], limitCount, 'union');
      // Filter to only published articles with public visibility (or no visibility = default public)
      return response.articles.filter(article => 
        article.status === 'published' && 
        (!article.visibility || article.visibility === 'public')
      );
    } catch (error) {
      handleSilentError(error, `ArticlesService.getPublishedArticlesByTag - Tag ${tagId}`);
      return [];
    }
  }

  // getArticleBySlugAdmin DELETED (2026-08-28 rules hardening): zero callers, and its
  // slug-only list is not rules-admissible unauthenticated. Server code that needs a
  // drafts-included slug fetch uses getArticleBySlugServer (src/lib/server/articles-admin.ts).

  // Search articles by text
  async searchArticles(searchTerm: string, options: {
    tagId?: string;
    status?: 'draft' | 'published' | 'scheduled';
    limit?: number;
    includeUnpublished?: boolean;
  } = {}): Promise<Article[]> {
    const {
      tagId,
      status,
      limit: limitCount = 1000, // Much higher limit for search results to ensure we find matches in older content
      includeUnpublished = false,
    } = options;

    let q = query(collection(db!, this.collectionName));

    // NOTE: We filter isDeleted client-side to avoid index requirements

    // Add filters
    if (tagId) {
      q = query(q, where('tags', 'array-contains', tagId));
    }

    if (!includeUnpublished) {
      q = query(q, where('status', '==', 'published'));
    } else if (status) {
      q = query(q, where('status', '==', status));
    }

    // Order by updated date
    // OPTIMIZATION: If filtering by tag, avoid Firestore orderBy to prevent "Missing Index" errors
    // Since we fetch a reasonable limit, we can sort client-side
    if (!tagId) {
      q = query(q, orderBy('updatedAt', 'desc'));
    }

    q = query(q, limit(limitCount));

    const querySnapshot = await getDocs(q);
    const articles: Article[] = [];

    querySnapshot.docs.forEach((doc) => {
      const data = doc.data();

      // Client-side filter: Skip deleted articles
      if (data.isDeleted === true) {
        return;
      }

      articles.push({
        id: doc.id,
        ...stripVectors(data),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        publishedAt: data.publishedAt?.toDate(),
      } as Article);
    });

    // Client-side sort if we skipped Firestore sort
    if (tagId) {
      articles.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
    }

    // Get all articles and filter by search term locally
    const filteredArticles: Article[] = [];
    articles.forEach((article) => {
      // Local text search in title, excerpt, content, and author
      const searchFields = [
        article.title,
        article.excerpt,
        article.content,
        article.author,
        // Include tag IDs in search - this will be improved when we add tag name resolution
        ...(article.tags || [])
      ].join(' ').toLowerCase();

      if (searchFields.includes(searchTerm.toLowerCase())) {
        filteredArticles.push(article);
      }
    });

    // Sort by relevance (title matches first, then excerpt, then content)
    const searchLower = searchTerm.toLowerCase();
    return filteredArticles.sort((a, b) => {
      const aTitle = a.title.toLowerCase().includes(searchLower);
      const bTitle = b.title.toLowerCase().includes(searchLower);
      const aExcerpt = a.excerpt.toLowerCase().includes(searchLower);
      const bExcerpt = b.excerpt.toLowerCase().includes(searchLower);

      if (aTitle && !bTitle) return -1;
      if (!aTitle && bTitle) return 1;
      if (aExcerpt && !bExcerpt) return -1;
      if (!aExcerpt && bExcerpt) return 1;

      // If equal relevance, sort by update date
      return b.updatedAt.getTime() - a.updatedAt.getTime();
    });
  }

  // Get featured article for a specific location (backwards compatibility)
  async getFeaturedArticle(location: 'root' | 'coins' | 'collections' | 'learn' | 'read'): Promise<Article | null> {
    try {
      let q = query(
        collection(db!, this.collectionName),
        where('status', '==', 'published'),
        where('isFeatured', '==', true),
        where('featuredLocation', '==', location),
        orderBy('featuredPriority', 'desc'),
        orderBy('createdAt', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.docs.length > 0) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        const article = {
          id: doc.id,
          ...stripVectors(data),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          publishedAt: data.publishedAt?.toDate(),
        } as Article;
        
        // Only return if public visibility
        if (!article.visibility || article.visibility === 'public') {
          return article;
        }
      }

      return null;
    } catch (error) {
      return handleDataFetchError(error, 'ArticlesService.getFeaturedArticle', location);
    }
  }

  // Get multiple featured articles for a specific location (new carousel system)
  async getFeaturedArticles(location: 'root' | 'coins' | 'collections' | 'learn' | 'read', maxArticles: number = 6): Promise<Article[]> {
    try {
      // First try the new system (featuredLocations array)
      let q1 = query(
        collection(db!, this.collectionName),
        where('status', '==', 'published'),
        where('isFeatured', '==', true),
        where('featuredLocations', 'array-contains', location),
        orderBy('createdAt', 'desc'),
        limit(maxArticles)
      );

      const querySnapshot1 = await getDocs(q1);

      if (querySnapshot1.docs.length > 0) {
        const articles = querySnapshot1.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...stripVectors(data),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            publishedAt: data.publishedAt?.toDate(),
          } as Article;
        })
        // Filter to only include public visibility articles
        .filter(article => !article.visibility || article.visibility === 'public');

        // Sort by location-specific priority if available
        return articles.sort((a, b) => {
          const aPriority = a.locationPriorities?.[location] || a.featuredPriority || 0;
          const bPriority = b.locationPriorities?.[location] || b.featuredPriority || 0;
          if (aPriority !== bPriority) {
            return bPriority - aPriority; // Higher priority first
          }
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
      }

      // Fallback to old system (featuredLocation single value)
      let q2 = query(
        collection(db!, this.collectionName),
        where('status', '==', 'published'),
        where('isFeatured', '==', true),
        where('featuredLocation', '==', location),
        orderBy('featuredPriority', 'desc'),
        orderBy('createdAt', 'desc'),
        limit(maxArticles)
      );

      const querySnapshot2 = await getDocs(q2);

      return querySnapshot2.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...stripVectors(data),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          publishedAt: data.publishedAt?.toDate(),
        } as Article;
      })
      // Filter to only include public visibility articles
      .filter(article => !article.visibility || article.visibility === 'public');

    } catch (error) {
      handleSilentError(error, `ArticlesService.getFeaturedArticles - Location: ${location}`);
      return [];
    }
  }

  // Get hero articles for homepage banner
  async getHeroArticles(maxArticles: number = 20): Promise<Article[]> {
    try {
      // Strategy: Direct query for hero articles sorted by publication date (newest first)
      // This ensures the carousel always shows the most recent hero articles
      const heroQuery = query(
        collection(db!, this.collectionName),
        where('isHero', '==', true),
        where('status', '==', 'published'),
        orderBy('publishedAt', 'desc'),
        limit(maxArticles)
      );

      const querySnapshot = await getDocs(heroQuery);

      if (!querySnapshot.empty) {
        const heroArticles = querySnapshot.docs
          .map(docSnap => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              ...stripVectors(data),
              isHero: true, // Guaranteed by query
              heroPosition: data.heroPosition || 999,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
              publishedAt: data.publishedAt?.toDate() || new Date(),
            } as Article;
          })
          // Filter to only include public visibility articles
          .filter(article => !article.visibility || article.visibility === 'public');

        console.log(`✅ Fetched ${heroArticles.length} hero articles sorted by publication date (public only)`);
        return heroArticles;
      }

      console.log('⚠️ No hero articles found via direct query, falling back to latest...');

      // Fallback: If no hero articles found (or index missing/error caught below), 
      // try to get latest articles as a fail-safe
      const response = await this.getArticles({
        limit: maxArticles,
        includeUnpublished: false
      });
      return response.articles;

    } catch (error) {
      console.warn('Error in getHeroArticles direct query (likely missing index), falling back to legacy method:', error);

      // Fallback Strategy: Get all published articles and filter/sort in memory
      // This works even if the specific hero index is missing
      try {
        const allPublishedQuery = query(
          collection(db!, this.collectionName),
          where('status', '==', 'published'),
          orderBy('publishedAt', 'desc'),
          limit(maxArticles * 4) // Increased limit to find older hero articles
        );

        const querySnapshot = await getDocs(allPublishedQuery);
        const allArticles: Article[] = [];

        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data) {
            allArticles.push({
              id: docSnap.id,
              ...stripVectors(data),
              isHero: data.isHero || false,
              heroPosition: data.heroPosition || 999,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
              publishedAt: data.publishedAt?.toDate() || new Date(),
            } as Article);
          }
        });

        // Filter by hero status AND public visibility, then sort by publication date
        const heroArticles = allArticles
          .filter(article => 
            article.isHero === true && 
            (!article.visibility || article.visibility === 'public')
          )
          .sort((a, b) => (b.publishedAt?.getTime() || 0) - (a.publishedAt?.getTime() || 0));

        if (heroArticles.length > 0) {
          return heroArticles.slice(0, maxArticles);
        }

        // If still no hero articles, just return the latest PUBLIC ones
        return allArticles
          .filter(article => !article.visibility || article.visibility === 'public')
          .slice(0, maxArticles);

      } catch (fallbackError) {
        handleSilentError(fallbackError, 'ArticlesService.getHeroArticles - Ultimate fallback');
        return [];
      }
    }
  }

  // Get articles with pagination and filters
  async getArticles(options: {
    tagId?: string;
    status?: 'draft' | 'published' | 'scheduled';
    limit?: number;
    lastDoc?: QueryDocumentSnapshot<DocumentData>;
    includeUnpublished?: boolean;
    hasQuiz?: boolean;
    isFeaturedQuiz?: boolean;
    isFeatured?: boolean;
    featuredLocation?: 'root' | 'coins' | 'collections' | 'learn' | 'read';
    authorId?: string;
  } = {}): Promise<ArticlesResponse> {
    const {
      tagId,
      status,
      limit: limitCount = 10,
      lastDoc,
      includeUnpublished = false,
      hasQuiz,
      isFeaturedQuiz,
      isFeatured,
      featuredLocation,
      authorId,
    } = options;

    const constraints = [];

    // Add filters
    if (tagId) {
      constraints.push(where('tags', 'array-contains', tagId));
    }

    if (authorId) {
      constraints.push(where('author', '==', authorId));
    }

    // For public access (when includeUnpublished is false), always ensure published status first
    // This helps with Firestore security rules by filtering published articles first
    if (!includeUnpublished) {
      constraints.push(where('status', '==', 'published'));
    } else if (status) {
      constraints.push(where('status', '==', status));
    }

    // Add quiz filters
    if (hasQuiz !== undefined) {
      constraints.push(where('hasQuiz', '==', hasQuiz));
    }

    if (isFeaturedQuiz !== undefined) {
      constraints.push(where('isFeaturedQuiz', '==', isFeaturedQuiz));
    }

    if (isFeatured !== undefined) {
      constraints.push(where('isFeatured', '==', isFeatured));
    }

    if (featuredLocation) {
      constraints.push(where('featuredLocation', '==', featuredLocation));
    }

    // Order by updated date
    // OPTIMIZATION: If filtering by tag, avoid Firestore orderBy to prevent "Missing Index" errors
    // Since we fetch a reasonable limit, we can sort client-side
    if (!tagId) {
      constraints.push(orderBy('updatedAt', 'desc'));
    }

    // C1 (defect register 2026-08-06) — FILTER BEFORE SLICING.
    //
    // The soft-delete filter used to run INSIDE the `index < limitCount` slice, so every
    // deleted doc among the first N silently ate a page slot: ask for 20, get 20-minus-
    // however-many-were-trashed. `hasMore` was computed off the UNFILTERED index, and the
    // `lastDoc` cursor was read at `docs[articles.length - 1]` — an index into the raw
    // docs using a count of the KEPT ones, so it also drifted backwards on any deleted row
    // and re-served rows on the next page.
    //
    // `isDeleted` is not a Firestore filter here on purpose (an `isDeleted != true` where-
    // clause would drop every doc that lacks the field, and legacy articles have none —
    // see the `!=` note in the repo's Firestore doctrine). So the fix is to OVER-FETCH:
    // keep pulling pages of limitCount+1 until we hold limitCount+1 *surviving* docs or
    // the query is exhausted. When nothing is deleted the first round is byte-identical
    // to the old single query — the extra reads are paid only when trash is actually in
    // the way, and are bounded by MAX_FETCH_ROUNDS.
    const MAX_FETCH_ROUNDS = 5;
    const wanted = limitCount + 1; // one extra: the hasMore probe
    const kept: QueryDocumentSnapshot<DocumentData>[] = [];
    let cursor: QueryDocumentSnapshot<DocumentData> | undefined = lastDoc;
    let exhausted = false;

    for (let round = 0; round < MAX_FETCH_ROUNDS && kept.length < wanted && !exhausted; round++) {
      const roundConstraints: QueryConstraint[] = [...constraints];
      if (cursor) roundConstraints.push(startAfter(cursor));
      roundConstraints.push(limit(wanted));

      const querySnapshot = await getDocs(
        query(collection(db!, this.collectionName), ...roundConstraints)
      );

      // Short read ⇒ we reached the end of the result set; no further round can help.
      if (querySnapshot.docs.length < wanted) exhausted = true;
      if (querySnapshot.docs.length === 0) break;

      for (const snap of querySnapshot.docs) {
        if ((snap.data() as DocumentData)?.isDeleted === true) continue;
        kept.push(snap);
      }
      cursor = querySnapshot.docs[querySnapshot.docs.length - 1];
    }

    // NOW slice: the page is the first limitCount SURVIVING docs, and hasMore is true
    // only when a surviving doc exists beyond them.
    const hasMore = kept.length > limitCount;
    const pageDocs = kept.slice(0, limitCount);

    const articles: Article[] = pageDocs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...stripVectors(data),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || data.createdAt?.toDate() || new Date(),
        publishedAt: data.publishedAt?.toDate(),
      } as Article;
    });

    // Client-side sort to match displayed dates:
    // - For published articles: sort by publishedAt (when they went live)
    // - For drafts: sort by updatedAt (recent work)
    articles.sort((a, b) => {
      const aDate = (a.status === 'published' && a.publishedAt) ? a.publishedAt : a.updatedAt;
      const bDate = (b.status === 'published' && b.publishedAt) ? b.publishedAt : b.updatedAt;
      return bDate.getTime() - aDate.getTime();
    });

    return {
      articles,
      hasMore,
      // C1 — the cursor is the last doc of the page in QUERY order (pageDocs is untouched
      // by the display sort above, which reorders `articles` only). Previously this indexed
      // the raw snapshot by the count of surviving articles, so any deleted doc shifted the
      // cursor backwards and the next page re-served rows already shown.
      lastDoc: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : undefined,
    };
  }

  /**
   * Exact count of published, non-deleted articles (optionally scoped to one tag), for honest
   * pagination `total` on the machine surface (/api/ai/news). A Firestore aggregate — reads the
   * count, not the docs — over the SAME equality filters getArticles uses (status published [+ tags
   * array-contains]), reusing the existing composite index. getArticles drops soft-deleted docs
   * CLIENT-side (isDeleted is usually an ABSENT field, so a `!= true` query would wrongly exclude
   * the majority), so we subtract the isDeleted==true count as a best-effort second aggregate to
   * match the served set exactly. The ?type= view-filter is NOT modelled (it is a view over this
   * corpus; the route keys hasMore off the fetched window in that case). Degrades to 0 on error.
   */
  async countPublishedArticles(tagId?: string): Promise<number> {
    if (!db) return 0;
    const coll = collection(db, this.collectionName);
    try {
      const base = tagId
        ? [where('tags', 'array-contains', tagId), where('status', '==', 'published')]
        : [where('status', '==', 'published')];
      const allSnap = await getCountFromServer(query(coll, ...base));
      let deleted = 0;
      try {
        const delSnap = await getCountFromServer(query(coll, ...base, where('isDeleted', '==', true)));
        deleted = delSnap.data().count;
      } catch {
        // No index for the isDeleted subset (rare, esp. with a tag) → report the marginally-high
        // published count rather than fail. Never the old fetched-window lie.
      }
      return Math.max(0, allSnap.data().count - deleted);
    } catch (error) {
      handleSilentError(error, 'ArticlesService.countPublishedArticles');
      return 0;
    }
  }


  // Soft delete an article (move to trash) (with RBAC security)
  async deleteArticle(id: string, currentUser: User, deletionReason?: string): Promise<void> {
    // Get existing article to check ownership
    const existingArticle = await this.getArticle(id);
    if (!existingArticle) {
      throw new Error('Article not found');
    }

    // Check permissions based on ownership
    const canDelete = RBACService.checkArticlePermission(currentUser, 'delete', existingArticle.author);
    if (!canDelete) {
      throw new Error(RBACService.getPermissionErrorMessage(Permission.ARTICLE_DELETE_ALL, currentUser.role));
    }

    // Writers can only delete their own drafts
    if (currentUser.role === 'writer') {
      if (existingArticle.status !== 'draft') {
        throw new Error('Writers can only delete draft articles. Published articles cannot be deleted.');
      }
      if (existingArticle.author !== currentUser.id) {
        throw new Error('You can only delete your own articles.');
      }
    }

    // Soft delete: mark as deleted instead of removing
    const docRef = doc(db!, this.collectionName, id);
    await updateDoc(docRef, {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: currentUser.id,
      deletionReason: deletionReason || null,
    });

    // Log activity
    try {
      const { activityLogService } = await import('./activity-log-service');
      await activityLogService.logArticleDeleted(
        currentUser,
        id,
        existingArticle.title,
        deletionReason
      );
    } catch (logError) {
      handleSilentError(logError, 'ArticlesService.deleteArticle - Activity logging');
    }

    // Remove from optimized collections if published
    if (existingArticle.status === 'published' && existingArticle.tags && existingArticle.tags.length > 0) {
      try {
        const { tagArticleCollectionsService } = await import('./tag-article-collections-service');
        await tagArticleCollectionsService.removeArticleFromTagCollections(id);
      } catch (collectionError) {
        handleSilentError(collectionError, 'ArticlesService.deleteArticle - Remove from optimized collections');
      }

      // Remove the article-tags junction rows too — the entity dossier's expanded
      // article path reads ONLY the junction (tagService.getExpandedArticles), and
      // soft-delete keeps status 'published', so a lingering row would keep the
      // trashed article on its entity dossiers.
      try {
        const { tagService } = await import('./tag-service');
        await tagService.removeAllTagsFromArticle(id);
      } catch (junctionError) {
        handleSilentError(junctionError, 'ArticlesService.deleteArticle - Remove article-tags junction rows');
      }

      // Decrement tag articleCount — a published article left the published set
      try {
        await tagService.adjustArticleCounts(existingArticle.tags, -1);
      } catch (tagCountError) {
        handleSilentError(tagCountError, 'ArticlesService.deleteArticle - Decrement tag articleCount');
      }
    }

    // CRITICAL: Revalidate Next.js ISR cache so deleted articles disappear from feeds immediately
    if (existingArticle.status === 'published') {
      try {
        const { revalidatePath, revalidateTag } = await import('next/cache');
        await revalidatePath('/');
        await revalidateTag('articles-list');
        await revalidateTag('read-data');
        
        // Revalidate tag pages for each tag the article had
        if (existingArticle.tags && existingArticle.tags.length > 0) {
          const { tagService } = await import('./tag-service');
          for (const tagId of existingArticle.tags) {
            try {
              const tag = await tagService.getTag(tagId);
              if (tag?.slug) {
                await revalidatePath(entityUrl(tag.slug, { trailingSlash: false }));
              }
            } catch (tagError) {
              // Continue with other tags if one fails
            }
          }
        }
        console.log('✅ Revalidated pages after deleting published article');
      } catch (revalidateError) {
        handleSilentError(revalidateError, 'ArticlesService.deleteArticle - On-demand revalidation');
      }
    }

    // Decrement article count if article was published (moved to trash = no longer visible)
    if (existingArticle.status === 'published') {
      try {
        const { userManagementService } = await import('./user-management-service');
        await userManagementService.decrementArticleCount(existingArticle.author);
      } catch (statsError) {
        handleSilentError(statsError, 'ArticlesService.deleteArticle - Decrement article count');
      }
    }

    // Trigger client-side cache invalidation for Recent Articles widgets
    try {
      // Dispatch a custom event to notify Recent Articles components to refresh
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('articleUpdated', {
          detail: { articleId: id, action: 'delete' }
        }));
      }
    } catch (eventError) {
      handleSilentError(eventError, 'ArticlesService.deleteArticle - Dispatch articleUpdated event');
    }
  }

  // Get trashed articles (owner/admin only)
  async getTrashedArticles(currentUser: User): Promise<Article[]> {
    // Check permissions - only admins and owners can view trash
    if (currentUser.role !== 'admin' && currentUser.role !== 'owner') {
      throw new Error('Only administrators can view trashed articles');
    }

    try {
      const q = query(
        collection(db!, this.collectionName),
        where('isDeleted', '==', true),
        orderBy('deletedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const articles: Article[] = [];

      querySnapshot.docs.forEach((doc) => {
        const data = doc.data();
        articles.push({
          id: doc.id,
          ...stripVectors(data),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          publishedAt: data.publishedAt?.toDate(),
          deletedAt: data.deletedAt?.toDate(),
        } as Article);
      });

      return articles;
    } catch (error) {
      throw handleUserError(error, 'Failed to retrieve trashed articles');
    }
  }

  // Restore article from trash
  async restoreArticle(id: string, currentUser: User): Promise<void> {
    // Check permissions - admins and owners can restore
    if (currentUser.role !== 'admin' && currentUser.role !== 'owner') {
      throw new Error('Only administrators can restore articles');
    }

    try {
      // Get the deleted article (bypass isDeleted filter)
      const docRef = doc(db!, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Article not found');
      }

      const articleData = docSnap.data();
      if (!articleData.isDeleted) {
        throw new Error('Article is not in trash');
      }

      // Restore: remove deletion fields
      await updateDoc(docRef, {
        isDeleted: false,
        deletedAt: null,
        deletedBy: null,
        deletionReason: null,
      });

      // Update user stats (increment article count ONLY if article is published)
      if (articleData.status === 'published') {
        try {
          const { userManagementService } = await import('./user-management-service');
          await userManagementService.incrementArticleCount(articleData.author);
        } catch (statsError) {
          handleSilentError(statsError, 'ArticlesService.restoreArticle - Update user stats');
        }

        // Re-increment tag articleCount — a published article is back in the set
        try {
          if (articleData.tags && articleData.tags.length > 0) {
            await tagService.adjustArticleCounts(articleData.tags, 1);
          }
        } catch (tagCountError) {
          handleSilentError(tagCountError, 'ArticlesService.restoreArticle - Increment tag articleCount');
        }
      }

      // Log activity
      try {
        const { activityLogService } = await import('./activity-log-service');
        await activityLogService.logArticleRestored(
          currentUser,
          id,
          articleData.title
        );
      } catch (logError) {
        handleSilentError(logError, 'ArticlesService.restoreArticle - Activity logging');
      }

      // Re-add to optimized collections if published
      if (articleData.status === 'published' && articleData.tags && articleData.tags.length > 0) {
        try {
          const { tagArticleCollectionsService } = await import('./tag-article-collections-service');
          const restoredArticle = {
            ...articleData,
            id,
            createdAt: articleData.createdAt?.toDate() || new Date(),
            updatedAt: articleData.updatedAt?.toDate() || new Date(),
            publishedAt: articleData.publishedAt?.toDate(),
            isDeleted: false,
          } as Article;

          await tagArticleCollectionsService.addArticleToTagCollections(
            id,
            restoredArticle,
            articleData.tags
          );
        } catch (collectionError) {
          handleSilentError(collectionError, 'ArticlesService.restoreArticle - Re-add to optimized collections');
        }
      }

      // Invalidate carousel caches
      try {
        const { carouselService } = await import('./carousel-service');
        carouselService.invalidateCarouselCaches();
      } catch (carouselCacheError) {
        handleSilentError(carouselCacheError, 'ArticlesService.restoreArticle - Invalidate carousel caches');
      }

      // Trigger client-side cache invalidation
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('articleUpdated', {
            detail: { articleId: id, action: 'restore' }
          }));
        }
      } catch (eventError) {
        handleSilentError(eventError, 'ArticlesService.restoreArticle - Dispatch articleUpdated event');
      }
    } catch (error) {
      throw handleUserError(error, 'Failed to restore article');
    }
  }

  // Permanently delete article (owner only)
  async permanentlyDeleteArticle(id: string, currentUser: User): Promise<void> {
    // Check permissions - only owners can permanently delete
    if (currentUser.role !== 'owner') {
      throw new Error('Only owners can permanently delete articles');
    }

    try {
      // Get the article (should be in trash)
      const docRef = doc(db!, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Article not found');
      }

      const articleData = docSnap.data();

      // Safety check: article should be in trash
      if (!articleData.isDeleted) {
        throw new Error('Article must be in trash before permanent deletion. Move to trash first.');
      }

      // Actually delete from Firestore
      await deleteDoc(docRef);

      // Note: Article count was already decremented when article was moved to trash
      // No need to decrement again on permanent deletion

      console.log(`🗑️ Permanently deleted article ${id}: ${articleData.title}`);
    } catch (error) {
      throw handleUserError(error, 'Failed to permanently delete article');
    }
  }

  // Auto-cleanup: Delete articles trashed >90 days ago
  async cleanupOldTrashedArticles(): Promise<number> {
    try {
      // Calculate 90 days ago
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

      const q = query(
        collection(db!, this.collectionName),
        where('isDeleted', '==', true),
        where('deletedAt', '<', Timestamp.fromDate(ninetyDaysAgo))
      );

      const querySnapshot = await getDocs(q);
      let deletedCount = 0;

      for (const docSnapshot of querySnapshot.docs) {
        try {
          await deleteDoc(doc(db!, this.collectionName, docSnapshot.id));
          deletedCount++;

          // Update user stats
          const articleData = docSnapshot.data();
          if (articleData.author) {
            try {
              const { userManagementService } = await import('./user-management-service');
              await userManagementService.decrementArticleCount(articleData.author);
            } catch (statsError) {
              handleSilentError(statsError, `ArticlesService.cleanupOldTrashedArticles - Update user stats for article ${docSnapshot.id}`);
            }
          }
        } catch (deleteError) {
          handleSilentError(deleteError, `ArticlesService.cleanupOldTrashedArticles - Delete article ${docSnapshot.id}`);
        }
      }

      console.log(`🗑️ Cleaned up ${deletedCount} old trashed articles`);
      return deletedCount;
    } catch (error) {
      handleSilentError(error, 'ArticlesService.cleanupOldTrashedArticles');
      return 0;
    }
  }

  // Get articles with RBAC filtering
  async getArticlesSecure(options: {
    tagId?: string;
    status?: 'draft' | 'published' | 'scheduled';
    limit?: number;
    lastDoc?: QueryDocumentSnapshot<DocumentData>;
    includeUnpublished?: boolean;
    hasQuiz?: boolean;
    isFeaturedQuiz?: boolean;
    isFeatured?: boolean;
    featuredLocation?: 'root' | 'coins' | 'collections' | 'learn' | 'read';
    scopeToAssignedTags?: boolean; // New parameter for editors to view all vs scoped
    authorId?: string; // Filter by author
  } = {}, currentUser: User | null): Promise<ArticlesResponse> {

    // Check if user has ARTICLE_READ_ALL granted (beyond their role)
    const hasReadAllPermission = currentUser?.grantedPermissions?.includes('article_read_all');

    // For writers without ARTICLE_READ_ALL, we need to make separate queries due to Firestore OR limitations
    if (currentUser?.role === 'writer' && !hasReadAllPermission) {
      return this.getArticlesForWriter(options, currentUser);
    }

    // For editors without ARTICLE_READ_ALL, filter by their assigned tags (unless explicitly viewing all)
    if (currentUser?.role === 'editor' && !hasReadAllPermission) {
      // Default to scoped view (own articles + assigned tags)
      const shouldScope = options.scopeToAssignedTags !== false;
      return this.getArticlesForEditor({ ...options, scopeToAssignedTags: shouldScope }, currentUser);
    }

    // For admins/owners OR users with ARTICLE_READ_ALL granted, use regular method with post-filtering
    const response = await this.getArticles(options);
    const filteredArticles = RBACService.filterArticlesByPermission(response.articles, currentUser);

    return {
      ...response,
      articles: filteredArticles,
    };
  }

  // Special method for writers to handle their restricted view
  private async getArticlesForWriter(options: {
    tagId?: string;
    status?: 'draft' | 'published' | 'scheduled';
    limit?: number;
    lastDoc?: QueryDocumentSnapshot<DocumentData>;
    includeUnpublished?: boolean;
    hasQuiz?: boolean;
    isFeaturedQuiz?: boolean;
    isFeatured?: boolean;
    featuredLocation?: 'root' | 'coins' | 'collections' | 'learn' | 'read';
  }, currentUser: User): Promise<ArticlesResponse> {
    const limitCount = options.limit || 10;
    // Writers can ONLY see their own articles in the admin dashboard
    // This is different from public-facing views where they might see published articles from others

    console.log('🔍 getArticlesForWriter called:', {
      userId: currentUser.id,
      userEmail: currentUser.email,
      statusFilter: options.status,
      tagFilter: options.tagId
    });

    // Handle specific status filters
    if (options.status === 'published') {
      // Only writer's own published articles
      return this.getWriterOwnArticles({
        ...options,
        authorId: currentUser.id!,
        status: 'published',
      });
    }

    if (options.status === 'draft') {
      // Only writer's own drafts
      return this.getWriterOwnArticles({
        ...options,
        authorId: currentUser.id!,
        status: 'draft',
      });
    }

    // For 'all' or no status filter, get all writer's own articles (both published and draft)

    // Articles fetched via getWriterOwnArticles method

    return this.getWriterOwnArticles({
      ...options,
      authorId: currentUser.id!,
      // Don't filter by status - get both published and draft
    });
  }

  // Helper method to get writer's own articles (drafts, published, or both)
  private async getWriterOwnArticles(options: {
    tagId?: string;
    authorId: string;
    status?: 'draft' | 'published' | 'scheduled';
    limit?: number;
    lastDoc?: QueryDocumentSnapshot<DocumentData>;
    hasQuiz?: boolean;
    isFeaturedQuiz?: boolean;
    isFeatured?: boolean;
    featuredLocation?: 'root' | 'coins' | 'collections' | 'learn' | 'read';
  }): Promise<ArticlesResponse> {
    const {
      tagId,
      authorId,
      limit: limitCount = 10,
      lastDoc,
      hasQuiz,
      isFeaturedQuiz,
      isFeatured,
      featuredLocation,
    } = options;

    console.log('🔍 getWriterOwnArticles: Querying with authorId:', authorId);

    try {
      let q = query(collection(db!, this.collectionName));

      // NOTE: We filter isDeleted client-side to avoid index requirements

      // Filter by author
      q = query(q, where('author', '==', authorId));

      // Add status filter if specified
      if (options.status) {
        q = query(q, where('status', '==', options.status));
        console.log('🔍 getWriterOwnArticles: Filtering by status:', options.status);
      }

      // Add additional filters
      if (tagId) {
        q = query(q, where('tags', 'array-contains', tagId));
      }

      if (hasQuiz !== undefined) {
        q = query(q, where('hasQuiz', '==', hasQuiz));
      }

      if (isFeaturedQuiz !== undefined) {
        q = query(q, where('isFeaturedQuiz', '==', isFeaturedQuiz));
      }

      if (isFeatured !== undefined) {
        q = query(q, where('isFeatured', '==', isFeatured));
      }

      if (featuredLocation) {
        q = query(q, where('featuredLocation', '==', featuredLocation));
      }

      // Order by created date
      q = query(q, orderBy('createdAt', 'desc'));

      // Add pagination
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      q = query(q, limit(limitCount + 1)); // Get one extra to check for more

      console.log('🔍 getWriterOwnArticles: About to execute query...');
      const querySnapshot = await getDocs(q);
      console.log('✅ getWriterOwnArticles: Query succeeded! Found', querySnapshot.size, 'articles');

      const articles: Article[] = [];
      let hasMore = false;

      querySnapshot.docs.forEach((doc, index) => {
        if (index < limitCount) {
          const data = doc.data();

          // Client-side filter: Skip deleted articles
          if (data.isDeleted === true) {
            return;
          }

          articles.push({
            id: doc.id,
            ...stripVectors(data),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate(),
            publishedAt: data.publishedAt?.toDate(),
          } as Article);
        } else {
          hasMore = true;
        }
      });

      console.log('✅ getWriterOwnArticles: Returning', articles.length, 'articles');
      return {
        articles,
        hasMore,
        lastDoc: querySnapshot.docs[Math.min(limitCount - 1, querySnapshot.docs.length - 1)] || null,
      };
    } catch (error) {
      handleSilentError(error, `ArticlesService.getWriterOwnArticles - Author ${authorId}`);

      // Return empty results on error
      return {
        articles: [],
        hasMore: false,
        lastDoc: null,
      };
    }
  }

  // Special method for editors to see articles based on assigned tags
  private async getArticlesForEditor(options: {
    tagId?: string;
    status?: 'draft' | 'published' | 'scheduled';
    limit?: number;
    lastDoc?: QueryDocumentSnapshot<DocumentData>;
    includeUnpublished?: boolean;
    hasQuiz?: boolean;
    isFeaturedQuiz?: boolean;
    isFeatured?: boolean;
    featuredLocation?: 'root' | 'coins' | 'collections' | 'learn' | 'read';
    scopeToAssignedTags?: boolean;
  }, currentUser: User): Promise<ArticlesResponse> {
    const shouldScope = options.scopeToAssignedTags !== false; // Default to true

    // If not scoping or editor wants to see all, return all articles
    if (!shouldScope) {
      const response = await this.getArticles(options);
      return response;
    }

    // Scoped view: own articles + articles with assigned tags
    const editorTagSlugs = currentUser.assignedTags || [];

    // If editor has no tags and no articles, they can't see anything in scoped view
    if (editorTagSlugs.length === 0) {
      // Still allow them to see their own articles
      return this.getWriterOwnArticles({
        ...options,
        authorId: currentUser.id!,
      });
    }

    try {
      // Get all tags to map slugs to IDs
      const allTags = await tagService.getAllTags();
      const tagSlugToIdMap = new Map(allTags.map(tag => [tag.slug, tag.id]));

      // Convert editor's tag slugs to tag IDs
      const editorTagIds = editorTagSlugs
        .map(slug => tagSlugToIdMap.get(slug))
        .filter(Boolean) as string[];

      // Get all articles (we'll filter client-side for tag overlap and ownership)
      const response = await this.getArticles(options);

      // Filter to articles that:
      // 1. Are written by the editor OR
      // 2. Have at least one tag the editor manages
      const filteredArticles = response.articles.filter(article => {
        // Editor's own articles
        if (article.author === currentUser.id) {
          return true;
        }

        // Articles with matching tags
        const articleTagIds = article.tags || [];
        if (articleTagIds.length === 0) return false;

        // Check if any article tag matches editor's tags
        return articleTagIds.some(tagId => editorTagIds.includes(tagId));
      });

      return {
        ...response,
        articles: filteredArticles,
        hasMore: false, // Client-side filtering, no pagination
      };
    } catch (error) {
      console.error('Error in getArticlesForEditor:', error);
      return {
        articles: [],
        hasMore: false,
      };
    }
  }

  // Search articles with RBAC filtering
  async searchArticlesSecure(searchTerm: string, options: {
    tagId?: string;
    status?: 'draft' | 'published' | 'scheduled';
    limit?: number;
    includeUnpublished?: boolean;
  } = {}, currentUser: User | null): Promise<Article[]> {
    // For writers, use RBAC-aware method
    if (currentUser?.role === 'writer') {
      const response = await this.getArticlesForWriter({
        ...options,
        limit: 100, // Get more for search filtering
      }, currentUser);

      // Apply local text search filtering
      return response.articles.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt?.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, options.limit || 10);
    }

    // For admins/editors/unauthenticated users, use regular search with post-filtering
    const results = await this.searchArticles(searchTerm, options);
    return RBACService.filterArticlesByPermission(results, currentUser);
  }

  // Upload image to Firebase Storage with WebP conversion
  async uploadImage(file: File, articleId?: string): Promise<string> {
    try {
      // Process image - optimize while preserving GIF animation
      const processedImage: ProcessedImage = await imageProcessingService.processForArticleImage(file);

      // Generate filename with appropriate extension
      const optimizedFileName = processedImage.format === 'gif'
        ? imageProcessingService.generateOptimizedFilename(file.name, 'image/gif')
        : imageProcessingService.generateWebPFilename(file.name);
      const folderPath = articleId ? `articles/${articleId}` : 'articles/general';
      const imagePath = `${folderPath}/${optimizedFileName}`;

      // Upload processed image
      const imageRef = ref(storage!, imagePath);
      await uploadBytes(imageRef, processedImage.blob);
      const downloadURL = await getDownloadURL(imageRef);

      // Log optimization results
      const savings = imageProcessingService.calculateStorageSavings(
        processedImage.originalSize,
        processedImage.compressedSize
      );

      if (processedImage.format === 'gif') {
        // GIF image processed, skipping compression
      } else {
        // Image compressed successfully
      }

      return downloadURL;
    } catch (error) {
      throw handleUserError(error, 'Failed to process and upload image');
    }
  }

  // Delete image from Firebase Storage
  async deleteImage(imageUrl: string): Promise<void> {
    const imageRef = ref(storage!, imageUrl);
    await deleteObject(imageRef);
  }

  // Get all published articles by a specific author (for efficient revalidation)
  async getPublishedArticlesByAuthor(authorId: string): Promise<Article[]> {
    try {
      if (!db) {
        return [];
      }

      const q = query(
        collection(db!, this.collectionName),
        where('author', '==', authorId),
        where('status', '==', 'published'),
        orderBy('publishedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...stripVectors(data),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          publishedAt: data.publishedAt?.toDate(),
        } as Article;
      })
      // Filter to only include public visibility articles
      .filter(article => !article.visibility || article.visibility === 'public');
    } catch (error) {
      handleSilentError(error, `ArticlesService.getArticlesByAuthor - Author ${authorId}`);
      return [];
    }
  }

  // Get basic article info (id, slug) by author for revalidation  
  async getArticlePathsByAuthor(authorId: string): Promise<Array<{ id: string, slug: string }>> {
    try {
      if (!db) {
        return [];
      }

      const q = query(
        collection(db!, this.collectionName),
        where('author', '==', authorId),
        where('status', '==', 'published')
      );

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs
        .filter(doc => {
          const data = doc.data();
          // Only include public visibility articles
          return !data.visibility || data.visibility === 'public';
        })
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            slug: data.slug,
          };
        });
    } catch (error) {
      handleSilentError(error, `ArticlesService.getArticlePathsByAuthor - Author ${authorId}`);
      return [];
    }
  }

  // Get articles by multiple tags (LEGACY - used only as fallback)
  // @deprecated Use getArticlesByTagsOptimized() instead for better performance
  // This method is kept only as a safety fallback when optimized collections fail
  async getArticlesByTags(
    tagIds: string[],
    limitCount: number = 10,
    filters: {
      published?: boolean;
      featured?: boolean;
      sortBy?: 'createdAt' | 'publishedAt' | 'updatedAt';
      sortOrder?: 'asc' | 'desc';
    } = {}
  ): Promise<Article[]> {
    try {
      if (!db) {
        return [];
      }

      if (!tagIds || tagIds.length === 0) {
        return [];
      }

      // Use tag service to efficiently get articles by tags
      const { tagService } = await import('./tag-service');

      // Get article IDs for each tag and combine them
      const allArticleIds = new Set<string>();
      const articleWeights = new Map<string, number>();

      for (const tagId of tagIds) {
        try {
          const articleIds = await tagService.getArticlesByTag(tagId, limitCount * 2);

          articleIds.forEach((articleId, index) => {
            allArticleIds.add(articleId);
            // Higher weight for articles that appear first and match multiple tags
            const currentWeight = articleWeights.get(articleId) || 0;
            const newWeight = currentWeight + (limitCount * 2 - index) + (tagIds.length > 1 ? 10 : 0);
            articleWeights.set(articleId, newWeight);
          });
        } catch (error) {
          handleSilentError(error, `ArticlesService.getArticlesByTags - Tag ${tagId}`);
        }
      }

      if (allArticleIds.size === 0) {
        return [];
      }

      // Fetch article details for all found IDs
      const articlePromises = Array.from(allArticleIds).slice(0, limitCount * 3).map(id =>
        this.getPublishedArticle(id).catch(error => {
          return null;
        })
      );

      const articles = (await Promise.all(articlePromises))
        .filter((article): article is Article => {
          if (!article) return false;

          // Apply filters
          if (filters.published && article.status !== 'published') return false;
          if (filters.featured && !article.isFeatured) return false;

          return true;
        });


      // Sort articles by weight (relevance) and then by date
      const sortedArticles = articles.sort((a, b) => {
        const aWeight = articleWeights.get(a.id || '') || 0;
        const bWeight = articleWeights.get(b.id || '') || 0;

        if (aWeight !== bWeight) {
          return bWeight - aWeight; // Higher weight first
        }

        // If weights are equal, sort by specified criteria
        const sortBy = filters.sortBy || 'createdAt';
        const sortOrder = filters.sortOrder || 'desc';

        const aDate = sortBy === 'publishedAt' ? (a.publishedAt || a.createdAt) :
          sortBy === 'updatedAt' ? (a.updatedAt || a.createdAt) : a.createdAt;
        const bDate = sortBy === 'publishedAt' ? (b.publishedAt || b.createdAt) :
          sortBy === 'updatedAt' ? (b.updatedAt || b.createdAt) : b.createdAt;

        return sortOrder === 'desc'
          ? bDate.getTime() - aDate.getTime()
          : aDate.getTime() - bDate.getTime();
      });

      return sortedArticles.slice(0, limitCount);

    } catch (error) {
      handleSilentError(error, `ArticlesService.getArticlesByTags - Tags: ${tagIds.join(', ')}`);
      return [];
    }
  }

  // SB-R1 / Option B (checkpoint-1 adjudication 2026-07-16): the `tag-articles/{tagId}/articles`
  // subcollection READ is RETIRED here. Previously the subcollection was read FIRST (both the
  // single- and multi-tag branches), and ONLY its EMPTINESS fell through to the canonical `articles`
  // array-contains query — so a stale-but-populated subcollection SHADOWED freshly-published
  // articles. The pipeline never writes that subcollection (only the admin-UI client paths in
  // `tagArticleCollectionsService.addArticleToTagCollections` do), so every drip-published article
  // was invisible to feeds + the dossier no-associations fallback: the 2026-07-09 Cambria gap —
  // 10 admin-era rows frozen at 2026-06-23 hid the 2026-07-02 / 2026-07-09 drip articles. We now
  // delegate straight to the array-contains fallback for BOTH cases (the composite index
  // `status ASC | tags CONTAINS | publishedAt DESC` is READY in prod and already served every
  // empty-subcollection tag). The method signature + `ArticlesResponse` shape are preserved so the
  // five consumers (dossier fallback, coins/collections/tags feeds, carousel) need NO change.
  //
  // `mergeStrategy` is now INERT — the fallback merges by published-recency (union-by-recency),
  // which is the correct order for feeds/carousel and is exactly what every empty-subcollection tag
  // already received in prod. The parameter is kept for signature compatibility; a later leg removes
  // it together with the now-dead write machinery (tag-article-collections-service; K2b precedent:
  // retire the denorm READ first, delete the dead store on next touch).
  async getArticlesByTagsOptimized(
    tagIds: string[],
    limitCount: number = 20,
    mergeStrategy: 'union' | 'weighted' | 'intersection' = 'weighted'
  ): Promise<ArticlesResponse> {
    void mergeStrategy; // INERT under Option B (kept for signature compatibility) — see method header.
    try {
      if (!db) {
        return { articles: [], hasMore: false };
      }

      if (!tagIds || tagIds.length === 0) {
        // If no tags specified, return regular articles
        return this.getArticles({ limit: limitCount, includeUnpublished: false });
      }

      // Canonical read: the fresh `articles` array-contains query (was the fallback; now the ONLY
      // path). No subcollection read → no stale-shadow. Same shape for single- and multi-tag.
      return this.getArticlesByTagsOptimizedFallback(tagIds, limitCount);
    } catch (error) {
      handleSilentError(error, `ArticlesService.getArticlesByTagsOptimized - Tags: ${tagIds.join(', ')}`);
      return this.getArticlesByTagsOptimizedFallback(tagIds, limitCount);
    }
  }

  // Fallback method using original array-contains-any approach
  private async getArticlesByTagsOptimizedFallback(tagIds: string[], limitCount: number = 20): Promise<ArticlesResponse> {
    try {
      // Use array-contains-any for efficient querying (max 10 tags per query)
      const tagBatches = [];
      for (let i = 0; i < tagIds.length; i += 10) {
        tagBatches.push(tagIds.slice(i, i + 10));
      }

      const allArticles = new Set<Article>();

      // Process tag batches in parallel
      const batchPromises = tagBatches.map(async (tagBatch) => {
        const q = query(
          collection(db!, this.collectionName),
          where('status', '==', 'published'),
          where('tags', 'array-contains-any', tagBatch),
          orderBy('publishedAt', 'desc'),
          limit(limitCount * 2) // Get more to allow for deduplication
        );

        try {
          const querySnapshot = await getDocs(q);
          return querySnapshot.docs
            .map(doc => {
              const data = doc.data();

              // Client-side filter: Skip deleted articles
              if (data.isDeleted === true) {
                return null;
              }

              return {
                id: doc.id,
                ...stripVectors(data),
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
                publishedAt: data.publishedAt?.toDate(),
              } as Article;
            })
            .filter((article): article is Article => article !== null)
            // Filter to only include public visibility articles
            .filter(article => !article.visibility || article.visibility === 'public');
        } catch (error) {
          handleSilentError(error, `ArticlesService.getArticlesByTagsOptimizedFallback - Batch query`);
          return [];
        }
      });

      const batchResults = await Promise.all(batchPromises);

      // Combine and deduplicate articles
      const articleMap = new Map<string, Article>();
      batchResults.flat().forEach(article => {
        if (!articleMap.has(article.id || '')) {
          articleMap.set(article.id || '', article);
        }
      });

      // Sort by published date and limit results
      const sortedArticles = Array.from(articleMap.values())
        .sort((a, b) => {
          const aDate = a.publishedAt || a.createdAt;
          const bDate = b.publishedAt || b.createdAt;
          return bDate.getTime() - aDate.getTime();
        })
        .slice(0, limitCount);

      return {
        articles: sortedArticles,
        hasMore: articleMap.size > limitCount
      };

    } catch (error) {
      handleSilentError(error, 'ArticlesService.getArticlesByTagsOptimizedFallback');
      return { articles: [], hasMore: false };
    }
  }
}

// Collections Service
export class CollectionsService {
  private collectionName = 'collections';

  // Upload featured image for a collection
  async uploadFeaturedImage(file: File, collectionSlug: string): Promise<string> {
    const timestamp = Date.now();
    const fileName = `${timestamp}-${file.name}`;
    const imagePath = `collections/${collectionSlug}/${fileName}`;

    const imageRef = ref(storage!, imagePath);
    await uploadBytes(imageRef, file);
    const downloadURL = await getDownloadURL(imageRef);

    return downloadURL;
  }

  // Delete image from Firebase Storage
  async deleteImage(imageUrl: string): Promise<void> {
    const imageRef = ref(storage!, imageUrl);
    await deleteObject(imageRef);
  }
}

// Export service instances
export const articlesService = new ArticlesService();
export const collectionsService = new CollectionsService();

